import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { userManagementApi } from '../../../services/userManagementApi';
import { User, SearchUsersRequest, PagedUsersResponse } from '../../../types/userManagement';
import UserList from './UserList';
import UserModal from './UserModal';
import './UserManagement.css';

const UserManagement: React.FC = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize] = useState(20);
  
  // Filtri
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [statusFilter, setStatusFilter] = useState<boolean | undefined>(undefined);
  
  // Modal
  const [showModal, setShowModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [modalMode, setModalMode] = useState<'create' | 'edit' | 'view'>('view');
  
  // Ruoli disponibili
  const [availableRoles, setAvailableRoles] = useState<string[]>([]);

  useEffect(() => {
    loadAvailableRoles();
  }, []);

  useEffect(() => {
    loadUsers();
  }, [currentPage, searchTerm, selectedRole, statusFilter]);

  const loadAvailableRoles = async () => {
    try {
      const roles = await userManagementApi.getAvailableRoles();
      setAvailableRoles(roles);
    } catch (error: any) {
      console.error('Errore nel caricamento ruoli:', error);
    }
  };

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const searchRequest: SearchUsersRequest = {
        searchTerm: searchTerm.trim() || undefined,
        role: selectedRole || undefined,
        isActive: statusFilter,
        page: currentPage,
        pageSize
      };
      
      const response: PagedUsersResponse = await userManagementApi.searchUsers(searchRequest);
      
      setUsers(response.users);
      setTotalCount(response.totalCount);
      setTotalPages(response.totalPages);
    } catch (error: any) {
      setError(error.message);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
    loadUsers();
  };

  const handleResetFilters = () => {
    setSearchTerm('');
    setSelectedRole('');
    setStatusFilter(undefined);
    setCurrentPage(1);
  };

  const handleCreateUser = () => {
    setSelectedUser(null);
    setModalMode('create');
    setShowModal(true);
  };

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setModalMode('edit');
    setShowModal(true);
  };

  const handleViewUser = (user: User) => {
    setSelectedUser(user);
    setModalMode('view');
    setShowModal(true);
  };

  const handleDeleteUser = async (userId: string) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo utente?')) {
      return;
    }

    try {
      await userManagementApi.deleteUser(userId);
      await loadUsers();
    } catch (error: any) {
      alert(`Errore nell'eliminazione: ${error.message}`);
    }
  };

  const handleToggleStatus = async (userId: string, isActive: boolean) => {
    try {
      await userManagementApi.toggleUserStatus(userId, !isActive);
      await loadUsers();
    } catch (error: any) {
      alert(`Errore nel cambio stato: ${error.message}`);
    }
  };

  const handleUnlockUser = async (userId: string) => {
    try {
      await userManagementApi.unlockUser(userId);
      await loadUsers();
    } catch (error: any) {
      alert(`Errore nello sblocco: ${error.message}`);
    }
  };

  const handleModalSave = async () => {
    await loadUsers();
    setShowModal(false);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  if (loading && users.length === 0) {
    return (
      <div className="user-management">
        <div className="loading">Caricamento utenti...</div>
      </div>
    );
  }

  return (
    <div className="user-management">
      <div className="user-management-header">
        <button 
          className="btn btn-outline back-to-dashboard"
          onClick={() => navigate('/dashboard')}
        >
          ← Dashboard
        </button>
        <h1>Gestione Utenti</h1>
      </div>

      {/* Filtri */}
      <div className="filters-section">
        <div className="filters-row">
          <div className="filter-group">
            <label>Cerca:</label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Nome, cognome o email..."
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
          </div>
          
          <div className="filter-group">
            <label>Ruolo:</label>
            <select 
              value={selectedRole} 
              onChange={(e) => setSelectedRole(e.target.value)}
            >
              <option value="">Tutti i ruoli</option>
              {availableRoles.map(role => (
                <option key={role} value={role}>{role}</option>
              ))}
            </select>
          </div>
          
          <div className="filter-group">
            <label>Stato:</label>
            <select 
              value={statusFilter === undefined ? '' : statusFilter.toString()} 
              onChange={(e) => {
                const value = e.target.value;
                setStatusFilter(value === '' ? undefined : value === 'true');
              }}
            >
              <option value="">Tutti</option>
              <option value="true">Attivi</option>
              <option value="false">Inattivi</option>
            </select>
          </div>
          
          <div className="filter-actions">
            <button className="btn btn-secondary" onClick={handleSearch}>
              Cerca
            </button>
            <button className="btn btn-outline" onClick={handleResetFilters}>
              Reset
            </button>
          </div>
          
          <div className="new-user-action">
            <button 
              className="btn btn-primary"
              onClick={handleCreateUser}
            >
              Nuovo Utente
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {/* Lista utenti */}
      <UserList
        users={users}
        loading={loading}
        onEdit={handleEditUser}
        onView={handleViewUser}
        onDelete={handleDeleteUser}
        onToggleStatus={handleToggleStatus}
        onUnlock={handleUnlockUser}
      />

      {/* Paginazione */}
      {totalPages > 1 && (
        <div className="pagination">
          <div className="pagination-info">
            Pagina {currentPage} di {totalPages} ({totalCount} utenti totali)
          </div>
          <div className="pagination-controls">
            <button 
              className="btn btn-outline"
              disabled={currentPage <= 1}
              onClick={() => handlePageChange(currentPage - 1)}
            >
              Precedente
            </button>
            
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const pageNum = Math.max(1, currentPage - 2) + i;
              if (pageNum > totalPages) return null;
              
              return (
                <button
                  key={pageNum}
                  className={`btn ${pageNum === currentPage ? 'btn-primary' : 'btn-outline'}`}
                  onClick={() => handlePageChange(pageNum)}
                >
                  {pageNum}
                </button>
              );
            })}
            
            <button 
              className="btn btn-outline"
              disabled={currentPage >= totalPages}
              onClick={() => handlePageChange(currentPage + 1)}
            >
              Successiva
            </button>
          </div>
        </div>
      )}

      {/* Modal per creazione/modifica utente */}
      {showModal && (
        <UserModal
          user={selectedUser}
          mode={modalMode}
          availableRoles={availableRoles}
          onSave={handleModalSave}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
};

export default UserManagement;